import type {SxProps, Theme} from '@mui/material';
import type {ChipProps} from '@mui/material';
import {COLORS} from '../../style/colors-ts';

export const tagColors = [
    'default',
    'primary',
    'secondary',
    'success',
    'error',
    'info',
    'warning',
] as const;

export type TagColor = typeof tagColors[number];

const tagShades: Record<TagColor, {100: string; 500: string}> = {
    default: {100: COLORS.grey[100], 500: COLORS.black[600]},
    primary: {100: COLORS.primary[100], 500: COLORS.primary[500]},
    secondary: {100: COLORS.secondary[100], 500: COLORS.secondary[500]},
    success: {100: COLORS.success[100], 500: COLORS.success[500]},
    error: {100: COLORS.error[100], 500: COLORS.error[500]},
    warning: {100: COLORS.warning[100], 500: COLORS.warning[500]},
    info: {100: COLORS.mithraBlue[100], 500: COLORS.mithraBlue[500]},
};

export const getTagSx = (
    color: TagColor,
    variant: NonNullable<ChipProps['variant']> = 'filled',
): SxProps<Theme> => {
    const shades = tagShades[color];

    if (variant === 'outlined') {
        if (color === 'default') {
            return {
                '&&': {
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: COLORS.black[500],
                    borderColor: COLORS.grey[300],
                },
                '&:hover:not(.Mui-disabled)': {
                    backgroundColor: COLORS.white[900],
                },
                '& .MuiChip-icon': {
                    color: COLORS.black[500],
                },
                '& .MuiChip-deleteIcon, & .MuiChip-deleteIcon:hover, &:hover .MuiChip-deleteIcon': {
                    color: COLORS.black[500],
                },
            };
        }

        return {
            '&&': {
                borderRadius: '8px',
                color: shades[500],
                borderColor: shades[500],
            },
            '&:hover:not(.Mui-disabled)': {
                backgroundColor: shades[100],
            },
            '& .MuiChip-icon': {
                color: shades[500],
            },
            '& .MuiChip-deleteIcon, & .MuiChip-deleteIcon:hover, &:hover .MuiChip-deleteIcon': {
                color: shades[500],
            },
        };
    }

    return {
        '&&': {
            borderRadius: '8px',
            backgroundColor: shades[500],
            color: COLORS.white[900],
        },
        '& .MuiChip-icon': {
            color: COLORS.white[900],
        },
        '& .MuiChip-deleteIcon, & .MuiChip-deleteIcon:hover, &:hover .MuiChip-deleteIcon': {
            color: COLORS.white[900],
        },
    };
};

export const getTagBorderColor = (color: TagColor): string => tagShades[color][100];
